import React, { useState } from 'react';
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { useSignup } from '../hooks/useSignup';
import { resendVerificationEmail } from '../api/signup';
import type { SignupFormData } from '../types';

export const SignupForm: React.FC = () => {
  const { isLoading, isSuccess, successMessage, errors: apiErrors, handleSignup, clearError } = useSignup();
  const [formData, setFormData] = useState<SignupFormData>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    jobTitle: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string[] }>({});
  
  // Merge API errors and validation errors
  const errors = { ...validationErrors, ...apiErrors };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear both validation and API errors for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    if (apiErrors[name]) {
      clearError(name);
    }
  };

  const handleResendEmail = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResending(true);
    setResendMessage('');

    try {
      const response = await resendVerificationEmail(formData.email);
      setResendMessage(response.message);
    } catch (error: any) {
      setResendMessage('Failed to resend email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string[] } = {};

    // Required field validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = ['First name is required'];
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = ['Last name is required'];
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = ['Phone number is required'];
    } else if (!/^\d{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = ['Phone number must be exactly 10 digits'];
    }
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = ['Job title is required'];
    }
    if (!formData.email.trim()) {
      newErrors.email = ['Email is required'];
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = ['Please enter a valid email address'];
    }
    if (!formData.password) {
      newErrors.password = ['Password is required'];
    } else if (formData.password.length < 8) {
      newErrors.password = ['Password must be at least 8 characters'];
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = ['Please confirm your password'];
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = ['Passwords do not match'];
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any existing validation errors before validation
    setValidationErrors({});
    
    if (!validateForm()) {
      // Scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector('.border-red-500');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    await handleSignup(formData);
  };

  // Show success message instead of form
  if (isSuccess) {
    return (
      <div className="space-y-4">
        <Alert className="bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="font-semibold mb-1">Account Created Successfully!</p>
                <p className="text-sm">{successMessage}</p>
              </div>
            </div>
          </AlertDescription>
        </Alert>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <strong>Next Steps:</strong>
          </p>
          <ol className="text-sm text-blue-800 mt-2 ml-4 list-decimal space-y-1">
            <li>Check your email inbox for a verification link</li>
            <li>Click the link to verify your email address</li>
            <li>Once verified, you can log in to your account</li>
          </ol>
        </div>
        <div className="text-center space-y-2">
          <p className="text-sm text-gray-600">
            Didn't receive the email? Check your spam folder or{' '}
            <button
              onClick={handleResendEmail}
              disabled={isResending}
              className="text-primary-500 hover:text-primary-600 underline disabled:opacity-50"
            >
              {isResending ? 'Sending...' : 'resend verification email'}
            </button>
          </p>
          {resendMessage && (
            <p className="text-sm text-green-700 font-medium">
              {resendMessage}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {(errors.non_field_errors || Object.keys(validationErrors).length > 0 && !errors.non_field_errors) && (
        <Alert variant="destructive">
          <AlertDescription>
            {errors.non_field_errors ? 
              errors.non_field_errors.join(' ') : 
              'Please fix the errors below and try again.'}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <label htmlFor="firstName" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          First Name <span className="text-red-500">*</span>
        </label>
        <Input
          id="firstName"
          name="firstName"
          type="text"
          autoComplete="given-name"
          placeholder="First Name"
          required
          value={formData.firstName}
          onChange={handleInputChange}
          className={errors.firstName ? "border-red-500 focus-visible:ring-red-500" : ""}
        />
        {errors.firstName && (
          <p className="text-sm text-red-600 font-medium">{errors.firstName.join(' ')}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="lastName" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Last Name <span className="text-red-500">*</span>
        </label>
        <Input
          id="lastName"
          name="lastName"
          type="text"
          autoComplete="family-name"
          placeholder="Last Name"
          required
          value={formData.lastName}
          onChange={handleInputChange}
          className={errors.lastName ? "border-red-500 focus-visible:ring-red-500" : ""}
        />
        {errors.lastName && (
          <p className="text-sm text-red-600 font-medium">{errors.lastName.join(' ')}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="phoneNumber" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <Input
          id="phoneNumber"
          name="phoneNumber"
          type="tel"
          autoComplete="tel"
          placeholder="1234567890"
          required
          value={formData.phoneNumber}
          onChange={handleInputChange}
          className={errors.phoneNumber ? "border-red-500 focus-visible:ring-red-500" : ""}
        />
        {errors.phoneNumber && (
          <p className="text-sm text-red-600 font-medium">{errors.phoneNumber.join(' ')}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="jobTitle" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Job Title <span className="text-red-500">*</span>
        </label>
        <Input
          id="jobTitle"
          name="jobTitle"
          type="text"
          autoComplete="organization-title"
          placeholder="Job Title"
          required
          value={formData.jobTitle}
          onChange={handleInputChange}
          className={errors.jobTitle ? "border-red-500 focus-visible:ring-red-500" : ""}
        />
        {errors.jobTitle && (
          <p className="text-sm text-red-600 font-medium">{errors.jobTitle.join(' ')}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Email <span className="text-red-500">*</span>
        </label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="Email"
          required
          value={formData.email}
          onChange={handleInputChange}
          className={errors.email ? "border-red-500 focus-visible:ring-red-500" : ""}
        />
        {errors.email && (
          <p className="text-sm text-red-600 font-medium">{errors.email.join(' ')}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="password" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Password <span className="text-red-500">*</span>
        </label>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="new-password"
          placeholder="Minimum 8 characters"
          required
          value={formData.password}
          onChange={handleInputChange}
          className={errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}
        />
        {errors.password && (
          <p className="text-sm text-red-600 font-medium">{errors.password.join(' ')}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="confirmPassword" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Confirm Password <span className="text-red-500">*</span>
        </label>
        <Input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          placeholder="Confirm Password"
          required
          value={formData.confirmPassword}
          onChange={handleInputChange}
          className={errors.confirmPassword ? "border-red-500 focus-visible:ring-red-500" : ""}
        />
        {errors.confirmPassword && (
          <p className="text-sm text-red-600 font-medium">{errors.confirmPassword.join(' ')}</p>
        )}
      </div>

      <Button 
        type="submit" 
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? "Signing up..." : "Sign Up"}
      </Button>
    </form>
  );
};

